/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import React from 'react'

interface TabItem {
  label: string
  value: string
  count?: number
}

interface TabsProps {
  activeTab: string
  tabs: TabItem[]
  onTabClick: (value: string) => void
}

/**
 * Tabs component for search filtering
 * Displays horizontal tabs with counts and active state styling
 */
const Tabs: React.FC<TabsProps> = ({ activeTab, tabs, onTabClick }) => {
  console.log('🔄 Tabs: Rendering with activeTab', activeTab)

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        width: 100%;
        border-bottom: 1px solid ${colors.SECONDARY_100};
        position: relative;
      `}
      role='tablist'
      aria-label='Search filter tabs'
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.value
        const displayLabel = tab.label

        return (
          <button
            key={tab.value}
            type='button'
            role='tab'
            aria-selected={isActive}
            aria-controls={`search-panel-${tab.value}`}
            onClick={() => {
              console.log('🔄 Tabs: Tab clicked', tab.value)
              onTabClick(tab.value)
            }}
            css={css`
              background: transparent;
              border: none;
              border-bottom: ${isActive
                ? `2px solid ${colors.PRIMARY}`
                : '2px solid transparent'};
              cursor: pointer;
              padding: 8px;
              flex-shrink: 0;
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: ${isActive ? 700 : 400};
              line-height: 20.3px;
              color: ${isActive ? colors.NEUTRAL_0 : colors.NEUTRAL_50};
              text-transform: ${tab.value === '' ? 'none' : 'capitalize'};
              transition: all 0.2s ease;
              white-space: nowrap;
              margin-bottom: -1px;

              &:hover {
                color: ${isActive ? colors.NEUTRAL_0 : colors.NEUTRAL_10};
              }

              &:focus-visible {
                outline: 2px solid ${colors.PRIMARY};
                outline-offset: 2px;
                border-radius: 4px;
              }
            `}
          >
            {displayLabel}
          </button>
        )
      })}
    </div>
  )
}

export default Tabs
